/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import Select from "react-select";
import { DeleteSharp } from "@mui/icons-material";
import { PollSelect } from "src/utils/common";
import ScheduleEntryList from "../Notification/ScheduleEntryList";

const PAGE_SIZE = 10;

const P_TO_OPTIONS = [
  { label: "All", value: "0" },
  { label: "Orange Tick", value: "7" },
  { label: "Not Orange Tick", value: "8" }
];

function PollForm({
  getdata,
  setgetdata,
  answer,
  setanswer,
  editingPoll,
  showValidation,
  setShowValidation,
  isLoading,
  loginUser,
  onSubmit,
  onCancel
}) {
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const loadingRef = useRef(false);
  const pageRef = useRef(1);

  // Debounce search input -> debouncedSearch
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const fetchOptions = useCallback(
    async (type, page = 1, append = false, search = "") => {
      if (!type || type === 4 || type === "4") return;
      if (loadingRef.current) return;
      loadingRef.current = true;
      setIsLoadingOptions(true);
      try {
        const resp = await PollSelect(type, page, PAGE_SIZE, search);
        if (resp && resp.status === 1 && resp.data) {
          const newOpts = resp.data.map((e) => ({ value: e.value, label: e.name }));
          setOptions((prev) => (append ? [...prev, ...newOpts] : newOpts));
          setHasMore(newOpts.length >= PAGE_SIZE);
          pageRef.current = page + 1;
        } else {
          if (!append) setOptions([]);
          setHasMore(false);
        }
      } catch {
        if (!append) setOptions([]);
        setHasMore(false);
      } finally {
        setIsLoadingOptions(false);
        loadingRef.current = false;
      }
    },
    []
  );

  // Fetch when type or debounced search changes
  useEffect(() => {
    if (getdata.p_type && getdata.p_type !== 4 && getdata.p_type !== "4") {
      setOptions([]);
      setHasMore(true);
      pageRef.current = 1;
      fetchOptions(getdata.p_type, 1, false, debouncedSearch);
    } else {
      setOptions([]);
      setHasMore(false);
    }
  }, [getdata.p_type, debouncedSearch]);

  const handleMenuScrollToBottom = () => {
    if (hasMore && !isLoadingOptions) {
      fetchOptions(getdata.p_type, pageRef.current, true, debouncedSearch);
    }
  };

  const handleInputChange = (newValue, actionMeta) => {
    if (actionMeta.action === "input-change") {
      setSearchInput(newValue);
    }
    return newValue;
  };

  const onChangeFunction = (e) => {
    if (e.target.name === "p_type") {
      setSearchInput("");
      setDebouncedSearch("");
      setOptions([]);
      setHasMore(true);
      setgetdata({ ...getdata, [e.target.name]: e.target.value, p_type_id: "" });
    } else {
      setgetdata({ ...getdata, [e.target.name]: e.target.value });
    }
  };

  const addanswer = () => {
    setanswer([...answer, { value: "" }]);
  };

  const handleValueChange = (index, newValue) => {
    const updatedAnswer = [...answer];
    updatedAnswer[index].value = newValue;
    setanswer(updatedAnswer);
  };

  const deleteObject = (index) => {
    setanswer(answer.filter((_, i) => index !== i));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom className="" style={{ textAlign: "center" }}>
          {editingPoll ? "Edit Poll" : "Add New Poll"}
        </Typography>
        <hr style={{ marginBottom: "0", marginTop: "0" }} />
      </Grid>

      <Grid item xs={12}>
        <Stack spacing={3}>
          <FormLabel>Poll To</FormLabel>
          <RadioGroup
            row
            value={String(getdata.p_to || "0")}
            onChange={(e) => setgetdata({ ...getdata, p_to: e.target.value })}
            name="p_to"
          >
            {P_TO_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Stack spacing={1}>
          <FormLabel>Poll For</FormLabel>
          <RadioGroup row value={getdata.p_type} onChange={onChangeFunction} name="p_type">
            <FormControlLabel value="4" control={<Radio />} label="LVB Prime" />
            <FormControlLabel value="1" control={<Radio />} label="State" />
            <FormControlLabel value="2" control={<Radio />} label="City" />
          </RadioGroup>
        </Stack>
      </Grid>

      {getdata.p_type != 4 && getdata.p_type !== "4" && (
        <Grid item xs={12}>
          <Stack spacing={3}>
            <label>
              Select {getdata.p_type == 1 || getdata.p_type === "1" ? "State" : "City"}
            </label>
            <Select
              isMulti
              filterOption={null}
              value={
                options?.filter((opt) => {
                  const ids = (getdata.p_type_id || "")
                    .toString()
                    .replace(/^\[|\]$/g, "")
                    .split(",")
                    .filter(Boolean);
                  return ids.includes(String(opt.value));
                }) || []
              }
              onChange={(selected) => {
                const val = selected ? selected.map((s) => s.value).join(",") : "";
                setgetdata({ ...getdata, p_type_id: val });
              }}
              onInputChange={handleInputChange}
              onMenuScrollToBottom={handleMenuScrollToBottom}
              menuPortalTarget={document.body}
              options={options}
              placeholder="Type to search..."
              isLoading={isLoadingOptions}
              noOptionsMessage={() => (isLoadingOptions ? "Loading..." : "No options")}
            />
          </Stack>
        </Grid>
      )}

      <Grid item xs={12}>
        <Stack spacing={3}>
          <TextField
            required
            type="text"
            label="Question"
            fullWidth
            value={getdata?.question ? getdata?.question : ""}
            onChange={onChangeFunction}
            name="question"
          />
        </Stack>
      </Grid>

      <Grid item xs={12}>
        {answer?.map((ans, i) => (
          <Stack key={i} spacing={3} style={{ marginBottom: "15px" }}>
            <Grid container spacing={2}>
              <Grid item xs={11} style={{ paddingLeft: "0px" }}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  type="text"
                  label={`Answer ${i + 1}`}
                  onChange={(e) => handleValueChange(i, e.target.value)}
                  value={ans.value}
                />
              </Grid>
              <Grid item xs={1} className="d-flex align-item-center">
                <IconButton color="error" aria-label="delete" onClick={() => deleteObject(i)}>
                  <DeleteSharp />
                </IconButton>
              </Grid>
            </Grid>
          </Stack>
        ))}
        <Button onClick={addanswer} style={{ marginTop: "15px" }} variant="contained">
          Add Answer
        </Button>
      </Grid>

      {/* Schedule Entries */}
      <Grid item xs={12}>
        <ScheduleEntryList
          entries={getdata.schedule_entries || []}
          setEntries={(entries) => setgetdata({ ...getdata, schedule_entries: entries })}
          isEditMode={!!editingPoll}
        />
      </Grid>

      {/* submit and rest form state */}
      <Grid item xs={12}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          sx={{ marginRight: "5px" }}
          disabled={isLoading}
          onClick={onSubmit}
        >
          {isLoading ? "Submitting..." : "Submit"}
        </Button>
        <Button variant="outlined" color="error" onClick={onCancel}>
          Cancel
        </Button>
      </Grid>
    </Grid>
  );
}

export default PollForm;
